import { type ReactNode, useEffect, useMemo, useState } from "react"

import Modal from "@forento/ui/components/Modal"

import { ArrowIcon, CloseIcon } from "~/components/Icon"

interface UseImageCarousel {
	showIndex(index: number): void
	element: ReactNode
}
export default function useImageCarousel(images: { id: number; filePath: string }[]): UseImageCarousel {
	const [isOpen, setOpen] = useState(false)
	const [currentImageIndex, setCurrentImageIndex] = useState(0)

	useEffect(() => {
		if (!isOpen) return

		const abortController = new AbortController()

		window.addEventListener(
			"keydown",
			event => {
				if (event.key === "ArrowLeft" && currentImageIndex > 0) setCurrentImageIndex(currentImageIndex - 1)
				if (event.key === "ArrowRight" && currentImageIndex < images.length - 1)
					setCurrentImageIndex(currentImageIndex + 1)
			},
			{ signal: abortController.signal },
		)

		return () => abortController.abort()
	}, [currentImageIndex, images.length, isOpen])

	return useMemo(
		() => ({
			showIndex(index) {
				setCurrentImageIndex(index)
				setOpen(true)
			},
			element: (
				// The shared Modal supplies dialog semantics, focus handling and Escape to close.
				<Modal
					isOpen={isOpen}
					onClose={() => setOpen(false)}
					ariaLabel="Image carousel"
					className="relative bg-transparent!"
				>
					<button
						type="button"
						className="focus-visible:outline-primary absolute top-6 right-6 cursor-pointer rounded-full bg-black/50 p-2 transition-all duration-100 hover:bg-black/70 focus-visible:outline-2"
						onClick={() => setOpen(false)}
						aria-label="Close image carousel"
					>
						<CloseIcon className="h-6 w-6 text-white" />
					</button>
					{/* aria-disabled rather than removed or disabled at the ends, so focus stays on the button. */}
					<button
						type="button"
						className="focus-visible:outline-primary absolute top-1/2 left-6 -translate-y-1/2 cursor-pointer rounded-full bg-black/50 p-2 transition-all duration-100 hover:bg-black/70 focus-visible:outline-2 aria-disabled:cursor-default aria-disabled:opacity-40"
						onClick={() => currentImageIndex > 0 && setCurrentImageIndex(currentImageIndex - 1)}
						aria-disabled={currentImageIndex === 0}
						aria-label="Previous image"
					>
						<ArrowIcon direction="left" className="h-6 w-6 text-white" />
					</button>
					{/* aria-disabled rather than removed or disabled at the ends, so focus stays on the button. */}
					<button
						type="button"
						className="focus-visible:outline-primary absolute top-1/2 right-6 -translate-y-1/2 cursor-pointer rounded-full bg-black/50 p-2 transition-all duration-100 hover:bg-black/70 focus-visible:outline-2 aria-disabled:cursor-default aria-disabled:opacity-40"
						onClick={() =>
							currentImageIndex < images.length - 1 && setCurrentImageIndex(currentImageIndex + 1)
						}
						aria-disabled={currentImageIndex === images.length - 1}
						aria-label="Next image"
					>
						<ArrowIcon direction="right" className="h-6 w-6 text-white" />
					</button>
					<img
						src={images[currentImageIndex].filePath}
						alt={`Post image ${currentImageIndex + 1} of ${images.length}`}
						className="h-auto max-h-[90dvh] w-full max-w-5xl rounded-lg object-contain"
					/>
				</Modal>
			),
		}),
		[currentImageIndex, images, isOpen],
	)
}
